import { Link } from "react-router-dom";
import { MonthlyTrendsCard } from "@/components/monthly-trends-card";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrentUser } from "@/api/queries/me";
import { useDepartments, useDepartmentOverview } from "@/api/queries/departments";
import { useAppStore } from "@/store/app-store";
import { useAuth } from "@/auth/AuthProvider";
import { isRespaiAdmin } from "@/lib/permissions";
import { buildExposureInsights } from "@/lib/carcinogen-insights";

/**
 * Drill-down reached by clicking the "Carcinogen exposure (month by month)" card
 * on the overview. It re-renders the same trend larger and pairs it with a
 * plain-language interpretation — what the metric is, what the worst month meant
 * "at the time it happened", and what the current standing means "today". The
 * narrative is rule-based (see src/lib/carcinogen-insights.ts), not a generative
 * model, so the wording is auditable and never overstates a dose.
 */
export function ExposureInsightsPage() {
  const { identity } = useAuth();
  const me = useCurrentUser();
  const isPlatformAdmin = isRespaiAdmin(identity?.role);
  const selectedDeptId = useAppStore((s) => s.selectedDepartmentId);
  const { data: departments } = useDepartments();
  const selectedDept = departments?.find((d) => d.id === selectedDeptId);
  const deptId = selectedDeptId ?? me.data?.department?.id;
  const deptName = isPlatformAdmin
    ? selectedDept?.name ??
      (selectedDeptId === "all" ? "All departments" : "Select a department")
    : me.data?.department?.name ?? "Loading department…";
  const overview = useDepartmentOverview(deptId);
  const insights = overview.data
    ? buildExposureInsights(overview.data.monthly_trends)
    : null;

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <Link
          to="/overview"
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Department overview
        </Link>
        <h1 className="text-2xl font-semibold">
          Carcinogen exposure, explained
        </h1>
        <p className="text-sm text-muted-foreground">{deptName}</p>
      </header>

      {!deptId ? (
        isPlatformAdmin ? (
          <p className="text-sm text-muted-foreground">
            Select a department to view its carcinogen-exposure trend.
          </p>
        ) : (
          <Skeleton className="h-64" />
        )
      ) : overview.isLoading ? (
        <Skeleton className="h-64" />
      ) : overview.isError ? (
        <p className="text-sm text-destructive">
          Failed to load carcinogen-exposure data.
        </p>
      ) : overview.data && insights ? (
        <>
          {insights.headline ? (
            <p className="text-base">{insights.headline}</p>
          ) : null}

          <MonthlyTrendsCard
            data={overview.data.monthly_trends}
            chartClassName="h-48"
          />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">What this metric is</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{insights.about}</p>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  At the time it happened
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{insights.atTheTime}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">What it means today</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{insights.today}</p>
              </CardContent>
            </Card>
          </div>
        </>
      ) : null}
    </div>
  );
}
